/**
 * Caseload Tab Component
 * Patient caseload management for RN Command Center
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  AlertTriangle, AlertCircle, CheckCircle, ChevronRight
} from "lucide-react";

const CaseloadTab = ({
  isDark,
  caseload
}) => {
  const navigate = useNavigate();

  return (
    <ScrollArea className="h-[600px]">
      <div className="space-y-2">
        {caseload.map((patient) => (
          <Card
            key={patient.patient_id}
            className={isDark ? "bg-slate-800/50 border-slate-700" : "bg-white border-gray-200 shadow-sm"}
            data-testid={`caseload-patient-${patient.patient_id}`}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center border ${
                    patient.risk_level === 'high' ? "bg-red-500/10 border-red-500/30 text-red-500" :
                    patient.risk_level === 'medium' ? "bg-amber-500/10 border-amber-500/30 text-amber-500" :
                    "bg-green-500/10 border-green-500/30 text-green-500"
                  }`}>
                    {patient.risk_level === 'high' ? <AlertTriangle className="w-5 h-5" /> :
                     patient.risk_level === 'medium' ? <AlertCircle className="w-5 h-5" /> :
                     <CheckCircle className="w-5 h-5" />}
                  </div>
                  <div>
                    <h3 className="font-semibold">{patient.patient_name}</h3>
                    <p className={`text-sm capitalize ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                      {patient.current_phase.replace('_', ' ')} • BP{patient.benefit_period || 1}
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-6">
                  <div className="text-center">
                    <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>Compliance</p>
                    <p className={`font-semibold ${
                      patient.compliance_score >= 80 ? 'text-green-500' :
                      patient.compliance_score >= 60 ? 'text-amber-500' : 'text-red-500'
                    }`}>
                      {patient.compliance_score}%
                    </p>
                  </div>
                  
                  <div className="text-center">
                    <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>Open</p>
                    <p className="font-semibold">{patient.open_tasks}</p>
                  </div>
                  
                  <div className="text-center">
                    <p className={`text-xs ${isDark ? "text-slate-500" : "text-gray-400"}`}>Overdue</p>
                    <p className={`font-semibold ${patient.overdue_tasks > 0 ? 'text-red-500' : 'text-green-500'}`}>
                      {patient.overdue_tasks}
                    </p>
                  </div>
                  
                  <div className="w-24">
                    <Progress value={patient.compliance_score} className="h-2" />
                  </div>
                  
                  <Button size="sm" onClick={() => navigate(`/hos?patient=${patient.patient_id}`)}>
                    View <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </ScrollArea>
  );
};

export default CaseloadTab;
